import React, { useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import StartHereGraphic from '~/_domains/onboarding/StartHereGraphic';
import { ROUTES } from "~/constants/routes.constants";
import { PERMISSIONS } from "~/constants/permissions.constants";
import { getAllGraphPermissions } from "~/utils/permissions";
import { route } from "./routes";

export const getMenuItems = (user, links, linkCategories, failedEmailsCount, solicitationsCount, unlockedMenus, bankReconciliationPending, conferenceUrgentCount) => {
  const history = useHistory();
  const isFirstRender = React.useRef(true);
  const onboardingData = Array.isArray(user.onboarding_status) ? user.onboarding_status : [];
  const tenant = user.tenant || {};
  const isBetaTester = tenant.is_beta_tester || false;
  const tenant_id = user.tenant.id || null;

  const done = onboardingData.filter(item => item.status === 'done').reverse();
  const progress = Math.round((done.length / onboardingData.length) * 100);

  const userPermissions = user.permissions ? user.permissions : [];
  const hasPermission = (permission) => {
    if (Array.isArray(permission)) {
      return permission.some(perm => userPermissions.includes(perm));
    }
    return userPermissions.includes(permission);
  };

  const canViewLinks = user.permissions
    ? user.permissions.includes(PERMISSIONS.links.view)
    : false;

  const activeLinks = links.filter((link) => link.show_in_menu);

  const activeLinkCategories = linkCategories
    .filter((category) => category.show_in_menu)
    .map((category) => {
      const currentLinks = links.filter(
        (link) => link.link_category_id === category.id
      );
      return {
        ...category,
        currentLinks: currentLinks,
      };
    });
    
  const menuItems = [
    {
      key: route.dashboard,
      icon: "dashboard",
      title: "Dashboard",
      description: "Visão geral com gráficos e indicadores do escritório",
      link: route.dashboard,
      permission: getAllGraphPermissions(),
      disabled: false
    },
    {
      key: route.customers,
      icon: "team",
      title: "Clientes",
      description: "Gerencie sua carteira de clientes, dados cadastrais e documentos",
      link: route.customers,
      permission: PERMISSIONS.customers.view,
      section: true,
      sectionLabel: "Gestão",
      disabled: !unlockedMenus.includes(route.customers),
    },
    {
      key: "tarefas",
      icon: "schedule",
      title: "Tarefas",
      disabled: (
        !unlockedMenus.includes(route.tarefas.cadastro) &&
        !unlockedMenus.includes(route.checklist)  &&
        !unlockedMenus.includes(route.calendario.administrativo)  &&
        !unlockedMenus.includes(route.calendario.contabil)  &&
        !unlockedMenus.includes(route.calendario.fiscal)  &&
        !unlockedMenus.includes(route.calendario.pessoal)  &&
        !unlockedMenus.includes(route.calendario.auditoria)
      ),
      subMenu: [
        {
          key: route.calendario.administrativo,
          title: "Administrativo",
          description: "Tarefas e obrigações do departamento administrativo",
          link: route.calendario.administrativo,
          departmentId: 2,
          permission: PERMISSIONS.calendarTasks.view,
          icon: "solution",
          disabled: !unlockedMenus.includes(route.calendario.administrativo),
          section: true,
          sectionLabel: "Calendário",
        },
        {
          key: route.calendario.contabil,
          title: "Contábil",
          description: "Tarefas e obrigações do departamento contábil",
          link: route.calendario.contabil,
          departmentId: 3,
          permission: PERMISSIONS.calendarTasks.view,
          icon: "calculator",
          disabled: !unlockedMenus.includes(route.calendario.contabil),
        },
        {
          key: route.calendario.fiscal,
          title: "Fiscal",
          description: "Tarefas e obrigações do departamento fiscal",
          link: route.calendario.fiscal,
          departmentId: 4,
          permission: PERMISSIONS.calendarTasks.view,
          icon: "audit",
          disabled: !unlockedMenus.includes(route.calendario.fiscal),
        },
        {
          key: route.calendario.pessoal,
          title: "Pessoal",
          description: "Tarefas e obrigações do departamento pessoal",
          link: route.calendario.pessoal,
          departmentId: 5,
          permission: PERMISSIONS.calendarTasks.view,
          icon: "team",
          disabled: !unlockedMenus.includes(route.calendario.pessoal),
        },
        {
          key: route.calendario.auditoria,
          title: "Sucesso do Cliente",
          description: "Acompanhamento de satisfação e relacionamento com clientes",
          link: route.calendario.auditoria,
          departmentId: 6,
          permission: PERMISSIONS.calendarTasks.view,
          icon: "smile",
          disabled: !unlockedMenus.includes(route.calendario.auditoria),
        },
        {
          key: route.calendario.legalizacao,
          title: "Legalização",
          description: "Tarefas do departamento de legalização e abertura de empresas",
          link: route.calendario.legalizacao,
          departmentId: 1,
          permission: PERMISSIONS.calendarTasks.view,
          icon: "file-sync",
          disabled: true,
        },
        {
          key: "/solicitacoes",
          title: "Solicitações de NFs",
          description: "Acompanhe e gerencie solicitações de notas fiscais dos clientes",
          link: "/solicitacoes",
          permission: PERMISSIONS.solicitations.view,
          icon: "inbox",
          badge: solicitationsCount,
          badgeLink: '/solicitacoes',
          disabled: true,
          section: true,
          sectionLabel: "Controle",
        },
        {
          key: route.checklist,
          title: "Checklist",
          description: "Controle de tarefas recorrentes por cliente e departamento",
          link: route.checklist,
          permission: PERMISSIONS.checklist.view,
          icon: "check-square",
          disabled: !unlockedMenus.includes(route.checklist),
        },
        {
          key: route.tarefas.conferencia,
          title: "Conferência",
          description: "Confira o aviso das guias de pagamento aos clientes antes do vencimento",
          link: route.tarefas.conferencia,
          permission: PERMISSIONS.conference.view,
          icon: "alert",
          pulseDot: conferenceUrgentCount > 0,
          disabled: !unlockedMenus.includes(route.calendario.fiscal),
        },
        {
          key: route.tarefas.extratos,
          title: "Extratos dos clientes",
          description: "Extratos bancários que os clientes enviam pelo portal, mês a mês",
          link: route.tarefas.extratos,
          permission: PERMISSIONS.extratoBancario.view,
          icon: "bank",
          disabled: !unlockedMenus.includes(route.calendario.contabil),
        },
        {
          key: route.convencoes,
          title: "Convenções Coletivas",
          description: "Consulte e gerencie convenções coletivas de trabalho",
          link: route.convencoes,
          permission: PERMISSIONS.conventions.view,
          icon: "file-text",
          disabled: true,
        },
      ],
    },
    {
      key: "financeiro",
      icon: "calculator",
      title: "Financeiro",
      permission: PERMISSIONS.financial.monthlyPayments.view,
      disabled: true,
      subMenu: [
        {
          key: route.financeiro.painel,
          title: "Painel Financeiro",
          description: "Acompanhe os serviços extras por etapa e veja, visualmente, quantas parcelas já foram pagas",
          link: route.financeiro.painel,
          permission: PERMISSIONS.extraServices.view,
          icon: "dashboard",
          disabled: true,
          section: true,
          sectionLabel: "Painel",
        },
        {
          key: route.financeiro.contasReceber,
          title: "Contas a Receber",
          description: "Gerencie receitas e contas a receber",
          link: route.financeiro.contasReceber,
          permission: PERMISSIONS.financial.contasReceber.view,
          icon: "dollar",
          disabled: true,
          section: true,
          sectionLabel: "Receitas",
        },
        {
          key: route.financeiro.mensalidades,
          title: "Mensalidades",
          description: "Gerencie cobranças mensais, faturas e pagamentos dos clientes",
          link: route.financeiro.mensalidades,
          permission: PERMISSIONS.financial.monthlyPayments.view,
          icon: "dollar",
          disabled: true,
        },
        {
          key: route.financeiro.extras,
          title: "Extras",
          description: "Serviços extras e cobranças avulsas fora da mensalidade",
          link: route.financeiro.extras,
          permission: PERMISSIONS.extraServices.view,
          icon: "plus",
          disabled: true,
        },
        {
          key: route.financeiro.contasPagar,
          title: "Contas a Pagar",
          description: "Gerencie contas a pagar e despesas do escritório",
          link: route.financeiro.contasPagar,
          permission: PERMISSIONS.financial.contasPagar.view,
          icon: "wallet",
          disabled: true,
          section: true,
          sectionLabel: "Despesas",
        },
        {
          key: route.financeiro.comissoes,
          title: "Comissões",
          description: "Controle de comissões e repasses para parceiros",
          link: route.financeiro.comissoes,
          permission: PERMISSIONS.refunds.view,
          icon: "percentage",
          disabled: true,
        },
        {
          key: route.financeiro.bancos,
          title: "Bancos",
          description: "Cadastro e gestão de contas bancárias",
          link: route.financeiro.bancos,
          permission: PERMISSIONS.financial.bancos.view,
          icon: "bank",
          disabled: true,
          section: true,
          sectionLabel: "Outros",
        },
        {
          key: route.financeiro.conciliacao,
          title: "Conciliação Bancária",
          description: "Concilie extratos bancários com lançamentos do sistema",
          link: route.financeiro.conciliacao,
          permission: PERMISSIONS.financial.bankReconciliation.view,
          icon: "reconciliation",
          badge: bankReconciliationPending,
          disabled: true,
        },
        {
          key: route.financeiro.extrato,
          title: "Extrato",
          description: "Visão unificada de todas as movimentações financeiras",
          link: route.financeiro.extrato,
          permission: PERMISSIONS.financial.extrato.view,
          icon: "bar-chart",
          disabled: true,
        },
      ],
    },
    {
      key: "compliance",
      icon: "audit",
      title: "Compliance",
      section: true,
      sectionLabel: "Compliance",
      disabled: true,
      subMenu: [
        {
          key: route.societario.painel,
          title: "Painel Societário",
          description: "Visão geral das obrigações societárias dos clientes",
          link: route.societario.painel,
          permission: PERMISSIONS.corporate.view,
          icon: "dashboard",
          disabled: true,
          section: true,
          sectionLabel: "Societário",
        },
        {
          key: route.societario.certificados,
          title: "Certificados",
          description: "Controle de certificados digitais, vencimentos e renovações",
          link: route.societario.certificados,
          permission: PERMISSIONS.certificates.view,
          icon: "safety-certificate",
          disabled: true,
        },
        {
          key: route.societario.lembretes,
          title: "Lembretes",
          description: "Lembretes de procurações, alvarás e documentos com vencimento",
          link: route.societario.lembretes,
          permission: PERMISSIONS.procurations.view,
          icon: "bell",
          disabled: true,
        },
        {
          key: route.irpf,
          title: "IRPF",
          description: "Gestão de declarações de imposto de renda pessoa física",
          link: route.irpf,
          permission: PERMISSIONS.irpf.view,
          icon: "file-done",
          disabled: true,
          section: true,
          sectionLabel: "Fiscal",
        },
        {
          key: route.dividasTributarias,
          title: "Dívidas Tributárias",
          description: "Acompanhe débitos tributários e parcelamentos dos clientes",
          link: route.dividasTributarias,
          permission: PERMISSIONS.irpf.dividasTributarias.view,
          icon: "warning",
          disabled: true,
        },
      ],
    },
    ...(isBetaTester ? [{
      key: "/crm",
      icon: "project",
      title: "CRM",
      section: true,
      sectionLabel: "Comercial",
      subMenu: [
        {
          key: ROUTES["/crm/prospeccao"],
          title: "Prospecção Ativa",
          description: "Gerencie leads de prospecção ativa e acompanhe o funil",
          link: ROUTES["/crm/prospeccao"],
          permission: PERMISSIONS.prospecting.view,
          icon: "phone",
        },
        {
          key: ROUTES["/crm/leads"],
          title: "Prospecção Passiva",
          description: "Leads recebidos por formulários, site e indicações",
          link: ROUTES["/crm/leads"],
          permission: PERMISSIONS.leads.view,
          icon: "inbox",
        },
        {
          key: ROUTES["/crm/social-selling"],
          title: "Social Selling",
          description: "Conteúdos e estratégias de vendas via redes sociais",
          link: ROUTES["/crm/social-selling"],
          permission: PERMISSIONS.socialSelling.view,
          icon: "share-alt",
        },
        {
          key: route.crm.referralProgram,
          title: "Indicação Premiada",
          description: "Programa de indicações com recompensas para clientes",
          link: route.crm.referralProgram,
          permission: PERMISSIONS.referralProgram.view,
          icon: "gift",
        },
      ],
    }] : [
      {
        key: ROUTES["/crm/leads"],
        icon: "project",
        title: "CRM",
        section: true,
        sectionLabel: "Comercial",
        link: ROUTES["/crm/leads"],
        permission: PERMISSIONS.leads.view,
        disabled: true,
      },
    ]),
    ...(isBetaTester ? [{
      key: "marketing",
      icon: "notification",
      title: "Marketing",
      // Sem declarar a seção, o getSectionKey faz o item herdar a do anterior
      // — e Marketing acabava dentro de "Comercial", com o ícone errado aceso.
      section: true,
      sectionLabel: "Marketing",
      permission: PERMISSIONS.boards.view,
      subMenu: [
        {
          key: route.marketing.campanhas,
          title: "Campanhas",
          description: "E-mails para a base de clientes, com relatório de entrega",
          link: route.marketing.campanhas,
          permission: PERMISSIONS.marketing.campaigns.view,
          icon: "mail",
        },
        {
          key: route.marketing.missoes,
          title: "Missões",
          description: "Campanhas e missões de marketing para engajamento",
          link: route.marketing.missoes,
          permission: PERMISSIONS.boards.view,
          icon: "rocket",
        },
        {
          key: route.marketing.convites,
          title: "Convites",
          description: "Envie convites personalizados para eventos e campanhas",
          link: route.marketing.convites,
          permission: PERMISSIONS.boards.view,
          icon: "mail",
        },
      ],
    }] : []),
    ...(isBetaTester ? [{
      key: "/recuperacao-tributaria",
      icon: "reconciliation",
      title: "Recuperação Tributária",
      link: "/recuperacao-tributaria",
      permission: PERMISSIONS.leadsRT.view,
      disabled: true,
    }] : []),
    ...((tenant_id === 'real') ? [{
      key: route.v2LeadsInssObra,
      icon: "reconciliation",
      title: "INSS Obra",
      link: route.v2LeadsInssObra,
      permission: PERMISSIONS.leads.view,
      disabled: true,
    }] : []),
    {
      key: "itensEnviados",
      icon: "cloud-upload",
      title: "Itens Enviados",
      section: true,
      sectionLabel: "Recursos",
      badge: failedEmailsCount,
      badgeLink: route.itensEnviados.emailsFailed,
      disabled: (
        !unlockedMenus.includes(route.itensEnviados.documentos) &&
        !unlockedMenus.includes(route.itensEnviados.emails)
      ),
      subMenu: [
        {
          key: route.itensEnviados.documentos,
          title: "Documentos enviados",
          description: "Histórico de documentos enviados aos clientes",
          link: route.itensEnviados.documentos,
          permission: PERMISSIONS.documents.view,
          icon: "file",
          disabled: !unlockedMenus.includes(route.itensEnviados.documentos),
        },
        {
          key: route.itensEnviados.emails,
          title: "E-mails enviados",
          description: "Histórico e status de e-mails enviados pelo sistema",
          link: route.itensEnviados.emails,
          permission: PERMISSIONS.sentEmails.view,
          icon: "mail",
          disabled: !unlockedMenus.includes(route.itensEnviados.emails),
        },
      ],
    },
    {
      key: "relatorios",
      icon: "book",
      title: "Relatórios",
      disabled: true,
      subMenu: [
        {
          key: route.relatorios.inadimplentes,
          title: "Inadimplentes",
          description: "Relatório de clientes com pagamentos em atraso",
          link: route.relatorios.inadimplentes,
          permission: PERMISSIONS.reports.defaulters,
          icon: "warning",
          disabled: true,
          section: true,
          sectionLabel: "Financeiro",
        },
        {
          key: route.relatorios.prazos,
          title: "Prazos",
          description: "Acompanhe prazos de entrega de documentos e obrigações",
          link: route.relatorios.prazos,
          permission: PERMISSIONS.documentsDeadline.view,
          icon: "clock-circle",
          disabled: true,
        },
        {
          key: route.relatorios.honorarios,
          title: "Controle de honorários",
          description: "Relatório detalhado de honorários por cliente e período",
          link: route.relatorios.honorarios,
          permission: PERMISSIONS.honoraries.view,
          icon: "dollar",
          disabled: true,
          section: true,
          sectionLabel: "Desempenho",
        },
        {
          key: route.relatorios.feedbacks,
          title: "KPIs",
          description: "Indicadores de desempenho e métricas do escritório",
          link: route.relatorios.feedbacks,
          permission: PERMISSIONS.feedbacks.view,
          icon: "line-chart",
          disabled: true,
        },
        {
          key: route.relatorios.socios,
          title: "Quadro de Sócios",
          description: "Visualize a composição societária de todos os clientes",
          link: route.relatorios.socios,
          permission: PERMISSIONS.reports.partners,
          icon: "team",
          disabled: true,
          section: true,
          sectionLabel: "Societário",
        },
      ],
    },
    {
      key: "/plugins/forms",
      icon: "form",
      title: "Formulários",
      description: "Crie formulários inteligentes para capturar informações e automatizar processos",
      link: "/plugins/forms",
      permission: PERMISSIONS.plugins.view,
      disabled: true,
      section: true,
      sectionLabel: "Plugins",
    },
    {
      key: "/plugins/click-counter",
      icon: "link",
      title: "Contador de Cliques",
      description: "Acompanhe quantas vezes um link foi clicado, ideal para campanhas",
      link: "/plugins/click-counter",
      permission: PERMISSIONS.plugins.view,
      disabled: true,
    },
    {
      key: "/base-de-conhecimento",
      icon: "exclamation-circle",
      title: "Base de conhecimento",
      description: "Artigos, tutoriais e documentação de apoio",
      link: "/base-de-conhecimento",
      permission: PERMISSIONS.knowledgeBase.view,
      disabled: true,
      section: true,
      sectionLabel: "Outros",
    },
  ];

  if (canViewLinks) {
    if (isBetaTester) {
      menuItems.push({
        key: "links-shortcut",
        icon: "link",
        title: "Links de atalho",
        link: "/links",
        permission: PERMISSIONS.links.view,
        disabled: false,
      });
    }

    if (isBetaTester) {
      activeLinkCategories.forEach((category) => {
        if (category.currentLinks) {
          menuItems.push({
            key: `category-${category.id}`,
            icon: "link",
            title: category.name,
            subMenu: category.currentLinks.map((link) => ({
              key: `link-${link.id}`,
              title: link.title,
              link: link.address,
              iconImg: <img src={`http://www.google.com/s2/favicons?domain=${link.address}`} alt="Ícone do site" width={14} height={14} style={{ marginRight: 10 }} />,
              disabled: false,
            })),
            disabled: false,
          });
        }
      });
    }

    if(isBetaTester){
      activeLinks.forEach((link) => {
        menuItems.push({
          key: `link-${link.id}`,
          title: link.title,
          link: link.address,
          iconImg: <img src={`http://www.google.com/s2/favicons?domain=${link.address}`} alt="Ícone do site" width={14} height={14} style={{ marginRight: 10 }} />,
          disabled: false,
        });
      });
    }
  }

  if (onboardingData.length > 0) {
    menuItems.unshift({
      key: route.onboarding,
      icon: "rocket",
      title: "Comece agora",
      link: route.onboarding,
      component: <StartHereGraphic progress={progress} />,
      disabled: false,
    });
  } else {
    menuItems.forEach(item => {
      item.disabled = false;
      if (item.subMenu) {
        item.subMenu.forEach(subItem => {
          subItem.disabled = false;
          if(subItem.subMenu){
            subItem.subMenu.forEach(subSubItem => {
              subSubItem.disabled = false;
            });
          }
        });
      }
    });
  }

  // Recursively filter menu items by permission
  var filterByPermission = function(items) {
    return items.filter(function(item) {
      if (item.permission && !hasPermission(item.permission)) {
        return false;
      }
      if (item.subMenu) {
        item.subMenu = filterByPermission(item.subMenu);
      }
      return true;
    });
  };

  // remove menu items that user does not have permission
  let filteredMenuItems = filterByPermission(menuItems);

  // if item has no link and no subMenu, remove it
  filteredMenuItems = filteredMenuItems.filter(item => item.link || (item.subMenu && item.subMenu.length > 0));

  // if progress is 100%, consider all menu items as enabled
  if (progress === 100) {
    filteredMenuItems.forEach(item => {
      item.disabled = false;
      if (item.subMenu) {
        item.subMenu.forEach(subItem => {
          subItem.disabled = false;
          if(subItem.subMenu){
            subItem.subMenu.forEach(subSubItem => {
              subSubItem.disabled = false;
            });
          }
        });
      }
    });
  }

  const activeMenuItems = filteredMenuItems.filter(item => !item.disabled);
  const disabledMenuItems = filteredMenuItems.filter(item => item.disabled);

  const sortedMenuItems = [...activeMenuItems, ...disabledMenuItems];

  useEffect(() => {
    if (isFirstRender.current && window.location.pathname === '/') {
      const dashboardItem = menuItems.find(item => item.key === "/");
      if (dashboardItem && dashboardItem.disabled) {
        history.push('/onboarding');
      }
      isFirstRender.current = false;
    }
  }, []);

  return sortedMenuItems;
};
